import React, { useState, useEffect } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Button,
  Card,
  CardContent
} from '@mui/material';
import {
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

// Palette de couleurs BIAT - Version moderne et vivante
const COLORS = [
  '#065F46',  // Vert forêt principal
  '#10B981',  // Vert émeraude vif
  '#3B82F6',  // Bleu professionnel
  '#8B5CF6',  // Violet moderne
  '#EC4899',  // Rose moderne
  '#F59E0B',  // Ambre chaleureux
  '#EF4444',  // Rouge accent
  '#06B6D4',  // Cyan moderne
  '#14B8A6',  // Turquoise
  '#DC9B51'   // Or doux
];

// Palette pour les graphiques en barres avec dégradés
const GRADIENT_COLORS = {
  primary: ['#065F46', '#10B981'],      // Dégradé vert
  secondary: ['#3B82F6', '#60A5FA'],     // Dégradé bleu
  accent: ['#8B5CF6', '#A78BFA'],        // Dégradé violet
  success: ['#10B981', '#34D399'],        // Dégradé vert clair
  warning: ['#F59E0B', '#FBBF24'],       // Dégradé ambre
  info: ['#06B6D4', '#22D3EE']           // Dégradé cyan
};

const MOCK_DATA = {
  globalStats: {
    stats: {
      dossiersDemarres: 128,
      devisEnCours: 24,
      dossiersEnAttentePieces: 12,
      dossiersValides: 86
    }
  },
  statusStats: {
    stats: {
      complets: 95,
      incomplets: 33,
      enCours: 24,
      enAttente: 12,
      pourcentageCompletsPremierCoup: 74
    }
  },
  agentStats: {
    agents: [
      { agent: 'agent.workflow', tasksEnCours: 8, nbDossiers: 5 },
      { agent: 'm.lefebvre', tasksEnCours: 6, nbDossiers: 4 },
      { agent: 's.trabelsi', tasksEnCours: 4, nbDossiers: 3 },
      { agent: 'a.benali', tasksEnCours: 3, nbDossiers: 2 }
    ]
  },
  historyStats: {
    tempsMoyenObtentionPieces: 18,
    delaiMoyenElaborationDevis: 9,
    tempsMoyenParEtape: {
      'Récupérer les pièces justificatives': 18,
      'Demander le complément manquant': 6,
      'Élaborer un devis': 9
    },
    dossiersParJour: Array.from({ length: 10 }).map((_, idx) => ({
      date: `2024-03-${String(10 + idx).padStart(2, '0')}`,
      count: Math.floor(Math.random() * 8) + 2
    }))
  }
};

const DashboardBIAT = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [globalStats, setGlobalStats] = useState(null);
  const [statusStats, setStatusStats] = useState(null);
  const [agentStats, setAgentStats] = useState(null);
  const [historyStats, setHistoryStats] = useState(null);

  useEffect(() => {
    loadStats();
    // Auto-refresh toutes les 5 minutes
    const interval = setInterval(loadStats, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const loadStats = async () => {
    try {
      setLoading(true);
      setError('');

      // Simuler un chargement réseau
      await new Promise((resolve) => setTimeout(resolve, 600));

      setGlobalStats(MOCK_DATA.globalStats);
      setStatusStats(MOCK_DATA.statusStats);
      setAgentStats(MOCK_DATA.agentStats);
      setHistoryStats(MOCK_DATA.historyStats);
    } catch (err) {
      setError('Erreur lors du chargement des statistiques mockées');
    } finally {
      setLoading(false);
    }
  };

  const renderKPI = (title, value, unit = '') => (
    <Card>
      <CardContent>
        <Typography color="text.secondary" gutterBottom variant="body2">
          {title}
        </Typography>
        <Typography variant="h4">
          {value !== null && value !== undefined ? value : 'N/A'}
          {unit && ` ${unit}`}
        </Typography>
      </CardContent>
    </Card>
  );

  if (loading && !globalStats) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4">Dashboard BIAT Assurance</Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* KPI Section */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPI(
            'Dossiers démarrés',
            globalStats?.stats?.dossiersDemarres
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPI('Devis en cours', globalStats?.stats?.devisEnCours)}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPI(
            'Dossiers en attente de pièces',
            globalStats?.stats?.dossiersEnAttentePieces
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          {renderKPI('Dossiers validés', globalStats?.stats?.dossiersValides)}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {renderKPI(
            'Temps moyen obtention pièces',
            historyStats?.tempsMoyenObtentionPieces,
            'h'
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {renderKPI(
            'Délai moyen élaboration devis',
            historyStats?.delaiMoyenElaborationDevis,
            'h'
          )}
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          {renderKPI(
            '% Dossiers complets du premier coup',
            statusStats?.stats?.pourcentageCompletsPremierCoup,
            '%'
          )}
        </Grid>
      </Grid>

      {/* Graphiques Section */}
      <Grid container spacing={3}>
        {/* Histogramme - Dossiers créés par jour */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Dossiers créés par jour (30 derniers jours)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={historyStats?.dossiersParJour || []}>
                <defs>
                  <linearGradient id="colorDossiers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={GRADIENT_COLORS.primary[0]} stopOpacity={1} />
                    <stop offset="100%" stopColor={GRADIENT_COLORS.primary[1]} stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="date" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="count" 
                  fill="url(#colorDossiers)" 
                  name="Nombre de dossiers"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Pie Chart - Complets vs Incomplets */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Répartition : Complets vs Incomplets
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <defs>
                  <linearGradient id="colorComplets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={1} />
                    <stop offset="100%" stopColor="#34D399" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id="colorIncomplets" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#F59E0B" stopOpacity={1} />
                    <stop offset="100%" stopColor="#FBBF24" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <Pie
                  data={[
                    { name: 'Complets', value: statusStats?.stats?.complets || 0 },
                    { name: 'Incomplets', value: statusStats?.stats?.incomplets || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={90}
                  innerRadius={40}
                  fill="#8884d8"
                  dataKey="value"
                  stroke="#FFFFFF"
                  strokeWidth={2}
                >
                  <Cell key="cell-0" fill="url(#colorComplets)" />
                  <Cell key="cell-1" fill="url(#colorIncomplets)" />
                </Pie>
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Bar Chart - Temps moyen par étape */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Temps moyen par étape (en heures)
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart
                data={[
                  {
                    name: 'Récupérer pièces',
                    temps: historyStats?.tempsMoyenParEtape?.['Récupérer les pièces justificatives'] || 0
                  },
                  {
                    name: 'Demander complément',
                    temps: historyStats?.tempsMoyenParEtape?.['Demander le complément manquant'] || 0
                  },
                  {
                    name: 'Élaborer devis',
                    temps: historyStats?.tempsMoyenParEtape?.['Élaborer un devis'] || 0
                  }
                ]}
              >
                <defs>
                  <linearGradient id="colorTemps1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#3B82F6" stopOpacity={1} />
                    <stop offset="100%" stopColor="#60A5FA" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id="colorTemps2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#8B5CF6" stopOpacity={1} />
                    <stop offset="100%" stopColor="#A78BFA" stopOpacity={0.8} />
                  </linearGradient>
                  <linearGradient id="colorTemps3" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#06B6D4" stopOpacity={1} />
                    <stop offset="100%" stopColor="#22D3EE" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis 
                  dataKey="name" 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <YAxis 
                  tick={{ fill: '#6B7280', fontSize: 12 }}
                  stroke="#9CA3AF"
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#FFFFFF', 
                    border: '1px solid #E5E7EB',
                    borderRadius: '8px',
                    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.1)'
                  }}
                />
                <Legend />
                <Bar 
                  dataKey="temps" 
                  name="Temps moyen (h)"
                  radius={[8, 8, 0, 0]}
                >
                  {[
                    'url(#colorTemps1)',
                    'url(#colorTemps2)',
                    'url(#colorTemps3)'
                  ].map((fill, index) => (
                    <Cell key={`cell-${index}`} fill={fill} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Heatmap - Charge de travail par agent */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Charge de travail par agent
            </Typography>
            <Box sx={{ mt: 2 }}>
              {agentStats?.agents && agentStats.agents.length > 0 ? (
                <Grid container spacing={2}>
                  {agentStats.agents.map((agent, index) => {
                    const intensity = Math.min(agent.tasksEnCours / 10, 1); // Normaliser sur 10 tâches max
                    const colorIntensity = Math.round(intensity * 255);
                    return (
                      <Grid item xs={12} sm={6} key={index}>
                        <Box
                          sx={{
                            p: 2,
                            backgroundColor: intensity > 0.5 ? `rgba(6, 95, 70, ${0.3 + intensity * 0.6})` : `rgba(16, 185, 129, ${0.2 + intensity * 0.4})`,
                            borderRadius: 2,
                            border: '1px solid #D1FAE5',
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              boxShadow: '0 4px 12px rgba(6, 95, 70, 0.2)',
                              transform: 'translateY(-2px)'
                            }
                          }}
                        >
                          <Typography variant="subtitle1" fontWeight="bold">
                            {agent.agent}
                          </Typography>
                          <Typography variant="body2">
                            Tâches en cours: {agent.tasksEnCours}
                          </Typography>
                          <Typography variant="body2">
                            Dossiers: {agent.nbDossiers}
                          </Typography>
                        </Box>
                      </Grid>
                    );
                  })}
                </Grid>
              ) : (
                <Typography variant="body2" color="text.secondary" align="center">
                  Aucune donnée disponible
                </Typography>
              )}
            </Box>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default DashboardBIAT;

